/**
 * 页面上下文：让侧栏 Agent 知道用户「当前页面上有什么」。
 *
 * - 任意页面：自动采集路由 + 主内容区可见文本（无需改页面代码）
 * - 关键页面：可调用 usePageContext(() => ({ kind, items, selected_ids, ... })) 提供结构化数据，
 *   Agent 据此拿到准确的文章 / 任务 ID 并直接处理、发布
 */
import { onBeforeUnmount, ref, shallowRef } from 'vue'

const VISIBLE_TEXT_LIMIT = 20000
const ITEMS_LIMIT = 100

const providers = shallowRef(new Map())
let seq = 0
let routerRef = null

/** 页面是否提供了结构化数据（侧栏展示用） */
export const pageContextVersion = ref(0)

export function bindPageContextRouter(router) {
  routerRef = router
}

/** 在页面 setup() 中调用：注册结构化数据 getter，页面卸载时自动注销。 */
export function usePageContext(getter) {
  const id = ++seq
  const next = new Map(providers.value)
  next.set(id, getter)
  providers.value = next
  pageContextVersion.value++
  onBeforeUnmount(() => {
    const m = new Map(providers.value)
    m.delete(id)
    providers.value = m
    pageContextVersion.value++
  })
}

function plain(value, depth = 0) {
  if (value == null || typeof value !== 'object') return value
  if (depth > 4) return undefined
  if (Array.isArray(value)) return value.slice(0, ITEMS_LIMIT).map((v) => plain(v, depth + 1))
  const out = {}
  for (const [k, v] of Object.entries(value)) {
    if (typeof v === 'function') continue
    out[k] = plain(v, depth + 1)
  }
  return out
}

function collectStructured() {
  const getters = [...providers.value.values()]
  // 嵌套页面时以最后注册（最内层）的为准
  for (let i = getters.length - 1; i >= 0; i--) {
    try {
      const data = getters[i]?.()
      if (data && typeof data === 'object') return plain(data)
    } catch (e) {
      console.warn('[pageContext] provider failed', e)
    }
  }
  return null
}

function collectVisibleText() {
  if (typeof document === 'undefined') return ''
  const root = document.querySelector('main.main') || document.body
  const parts = [(root?.innerText || '').trim()]
  // 打开中的弹窗（文章详情 / 发布对话框等）也算页面内容
  document.querySelectorAll('.el-overlay:not([style*="display: none"]) .el-dialog, .el-drawer.open').forEach((el) => {
    const t = (el.innerText || '').trim()
    if (t) parts.push(`【弹窗】\n${t}`)
  })
  return parts
    .join('\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .slice(0, VISIBLE_TEXT_LIMIT)
}

/** 发送消息时调用：生成当前页面快照。 */
export function collectPageContext() {
  const route = routerRef?.currentRoute?.value
  const routeInfo = route
    ? {
        path: route.path,
        name: typeof route.name === 'string' ? route.name : String(route.name || ''),
        params: plain(route.params || {}),
        query: plain(route.query || {}),
      }
    : null
  return {
    route: routeInfo,
    page_name: routeInfo?.name || (typeof document !== 'undefined' ? document.title : ''),
    data: collectStructured(),
    visible_text: collectVisibleText(),
    captured_at: new Date().toISOString(),
  }
}

/** 侧栏标签：「内容库 · 已选 3」 */
export function describePageContext() {
  void pageContextVersion.value
  const route = routerRef?.currentRoute?.value
  const name = typeof route?.name === 'string' ? route.name : ''
  const data = collectStructured()
  const selected = data?.selected_ids?.length || 0
  const items = data?.items?.length || 0
  let detail = ''
  if (selected) detail = `已选 ${selected}`
  else if (items) detail = `${items} 条`
  return { name: name || route?.path || '', detail, structured: !!data }
}
